import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { PrimaryButton } from './buttons';

interface LoginProps {
  onSuccess?: () => void;
  showRegisterLink?: boolean;
  onShowRegister?: () => void;
}

const Login: React.FC<LoginProps> = ({ onSuccess, showRegisterLink = true, onShowRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const { showToast, ToastComponent } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await login({ email: email.trim(), password });
      showToast('Successfully logged in!', 'success');
      // Navigate to dashboard after successful login
      navigate('/dashboard');
      onSuccess?.();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Login failed', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center px-4">
      {ToastComponent}
      <div className="max-w-md w-full bg-[#27272A] border border-[#3F3F46] rounded-2xl p-8 shadow-card">
        <h2 className="text-2xl font-bold text-white mb-8 text-center">Login</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-content-secondary mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field w-full"
              placeholder="Enter your email"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-content-secondary mb-2">
              Password *
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field w-full"
              placeholder="Enter your password"
              required
              disabled={isSubmitting}
            />
          </div>

          <PrimaryButton
            type="submit"
            disabled={isSubmitting || !email.trim() || !password.trim()}
            className="w-full"
          >
            {isSubmitting ? 'Logging in...' : 'Login'}
          </PrimaryButton>
        </form>

        {showRegisterLink && (
          <div className="mt-6 pt-6 border-t border-[#3F3F46] text-center">
            <p className="text-[#9CA3AF]">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="text-[#14B8A6] hover:text-[#0D9488] font-medium transition-colors"
              >
                Register here
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
